<template>
  <div id="lavorazione">
    <HeroSlider
      v-if="lavorazioneHeroSliderContent"
      :slider-data="lavorazioneHeroSliderContent"
      :squareVideo="false"
    />
    <LazyHydrate
      when-visible
      v-if="
        lavorazioneTwoColumnsTextContent &&
        lavorazioneTwoColumnsTextContent.active
      "
    >
      <TwoBlocksDescription :data="lavorazioneTwoColumnsTextContent.data" />
    </LazyHydrate>
    <template
      v-if="
        lavorazioneImageBannerContent && lavorazioneImageBannerContent.active
      "
    >
      <WideBanner
        :data="lavorazioneImageBannerContent.data"
        name="lavorazioneVideo"
        key="lavorazioneVideo"
      />
    </template>
    <template
      v-if="
        lavorazioneMastersAtWorkContent &&
        lavorazioneMastersAtWorkContent.active
      "
    >
      <MastersAtWork :maw-data="lavorazioneMastersAtWorkContent.data" />
    </template>
  </div>
</template>

<script>
import {
  computed,
  defineComponent,
  ref,
  useContext,
  useFetch,
} from '@nuxtjs/composition-api';
import { useCache, CacheTagPrefix } from '@gemini/vsf-cache';
import HeroSlider from '~/components/Homepage/HeroSlider/HeroSlider.vue';
import LazyHydrate from 'vue-lazy-hydration';
import { useContent, contentGetters } from '@gemini-vsf/composables';
import WideBanner from '~/components/Editorial/Commons/WideBanner.vue';
import TwoBlocksDescription from '~/components/Editorial/Commons/TwoBlocksDescription.vue';
import MastersAtWork from '~/components/Editorial/Lavorazione/MastersAtWork.vue';
import { useWindow } from '~/composables';
import { handleSeo } from '~/helpers/seo/handleSeo';

export default defineComponent({
  name: 'LavorazionePage',
  components: {
    LazyHydrate,
    HeroSlider,
    WideBanner,
    TwoBlocksDescription,
    MastersAtWork,
  },
  setup() {
    const { addTags } = useCache();
    const {
      app: {
        i18n: {
          localeProperties: { localeValue },
        },
      },
    } = useContext();
    const { app } = useContext();
    const { isDesktop } = useWindow();
    const { blocks, loadBlocks } = useContent(`lavorazione-${localeValue}`);

    const lavorazioneHeroSliderSelector = `lavorazione-hero-slide-${localeValue}`;
    const lavorazioneHeroSliderContent = ref();

    const lavorazioneTwoColumnsTextSelector = `lavorazione-tct-${localeValue}`;
    const lavorazioneTwoColumnsTextContent = ref();

    const lavorazioneImageBannerSelector = `lavorazione-image-banner-${localeValue}`;
    const lavorazioneImageBannerContent = ref();

    const lavorazioneMastersAtWorkSelector = `maw-${localeValue}`;
    const lavorazioneMastersAtWorkContent = ref();

    useFetch(async () => {
      await loadBlocks({
        identifiers: [
          lavorazioneTwoColumnsTextSelector,
          lavorazioneHeroSliderSelector,
          lavorazioneImageBannerSelector,
          lavorazioneMastersAtWorkSelector,
        ],
      });
      const blockData = await contentGetters.getCmsBlockContent(blocks.value);
      const lavorazioneHeroSliderBlockData =
        blockData[lavorazioneHeroSliderSelector];
      if (
        lavorazioneHeroSliderBlockData &&
        lavorazioneHeroSliderBlockData.active
      ) {
        lavorazioneHeroSliderContent.value =
          lavorazioneHeroSliderBlockData.data;
        lavorazioneHeroSliderContent.value.active = true;
        if (lavorazioneHeroSliderContent.value.cta) {
          delete lavorazioneHeroSliderContent.value.cta;
        }
      }
      lavorazioneTwoColumnsTextContent.value =
        blockData[lavorazioneTwoColumnsTextSelector];
      lavorazioneImageBannerContent.value =
        blockData[lavorazioneImageBannerSelector];
      lavorazioneMastersAtWorkContent.value =
        blockData[lavorazioneMastersAtWorkSelector];
      addTags([
        { prefix: CacheTagPrefix.View, value: 'lavorazione' },
        { prefix: CacheTagPrefix.View, value: 'page' },
      ]);
    });

    const metaTitle = computed(() =>
      app.i18n.t('Murano Glass Factory in Venice - Sogni Di Cristallo®')
    );
    const metaDescription = computed(() => 'Lavorazione');
    const { getCanonical } = handleSeo();
    const canonicalLink = computed(() => getCanonical());

    return {
      isDesktop,
      metaTitle,
      metaDescription,
      canonicalLink,
      lavorazioneHeroSliderContent,
      lavorazioneTwoColumnsTextContent,
      lavorazioneImageBannerContent,
      lavorazioneMastersAtWorkContent,
    };
  },
  head() {
    return {
      title: this?.metaTitle,
      meta: [
        { hid: 'robots', name: 'robots', content: 'index, follow' },
        {
          hid: 'og:description',
          property: 'og:description',
          content: this?.metaDescription,
        },
        {
          hid: 'description',
          name: 'description',
          content: this?.metaDescription,
        },
        { hid: 'og:title', property: 'og:title', content: this?.metaTitle },
        //og:url
        {
          hid: 'og:url',
          property: 'og:url',
          content:
            'https://www.sognidicristallo.com/' +
            this?.localePath('/lavorazione'),
        },
        { hid: 'fb:app_id', property: 'fb:app_id', content: '208478719244524' },
      ],
      link: [{ ...this.canonicalLink }],
    };
  },
});
</script>

<style lang="scss">
#lavorazione {
  @include desktop-boxed;
  .hero-slider__slide__content {
    top: 31.21%;
    left: unset;
    transform: unset;
    @include from-desktop-min {
      top: 44.46%;
      left: unset;
      transform: unset;
    }
  }
  .tbd-container {
    &__text {
      display: flex;
      flex-direction: column;
      &__bold {
        padding-bottom: 2.5rem;
      }
      @include for-desktop {
        flex-direction: row;
        justify-content: space-between;
        &__bold {
          max-width: 27.0625rem;
          padding-bottom: unset;
        }
        &__paragraph {
          max-width: 45.125rem;
        }
      }
    }
  }
}
</style>
